<template>
  <div class="area-senha p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-key"></i>
      Alterar Senha
    </h5>

    <!-- Instruções de Segurança -->
    <div class="info-box mb-4">
      <h6>
        <i class="fas fa-shield-alt"></i>
        Dicas de Segurança
      </h6>
      <ul>
        <li><i class="fas fa-check-circle"></i> Use uma senha forte com pelo menos 6 caracteres</li>
        <li><i class="fas fa-check-circle"></i> Combine letras, números e caracteres especiais</li>
        <li><i class="fas fa-check-circle"></i> Não compartilhe sua senha com ninguém</li>
        <li><i class="fas fa-check-circle"></i> Evite usar informações pessoais óbvias</li>
      </ul>
    </div>

    <!-- Formulário -->
    <div class="row justify-content-center">
      <form @submit.prevent="cadastrarSenha" class="col-md-8 col-lg-6">
        <div class="senha-form-container">
          <!-- Senha Antiga -->
          <div class="form-group mb-4">
            <label class="form-label">
              <i class="fas fa-lock"></i>
              Senha Atual <span class="text-danger">*</span>
            </label>
            <div class="password-input-container">
              <FieldInput
                :type="mostrarSenhaAntiga ? 'text' : 'password'"
                required
                v-model="senhaAntiga"
                placeholder="Digite sua senha atual"
                class="senha-input"
              />
              <button 
                type="button" 
                class="btn-toggle-password"
                @click="mostrarSenhaAntiga = !mostrarSenhaAntiga"
              >
                <i :class="mostrarSenhaAntiga ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>

          <!-- Nova Senha -->
          <div class="form-group mb-3">
            <label class="form-label">
              <i class="fas fa-lock"></i>
              Nova Senha <span class="text-danger">*</span>
            </label>
            <div class="password-input-container">
              <FieldInput
                :type="mostrarSenhaNova ? 'text' : 'password'"
                required
                v-model="senhaNova"
                placeholder="Digite sua nova senha"
                @input="validarSenha"
                :class="{ 'is-invalid': erroSenha }"
                class="senha-input"
              />
              <button 
                type="button" 
                class="btn-toggle-password"
                @click="mostrarSenhaNova = !mostrarSenhaNova"
              >
                <i :class="mostrarSenhaNova ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <small v-if="erroSenha" class="text-danger d-block mt-2">
              <i class="fas fa-exclamation-circle"></i>
              {{ erroSenha }}
            </small>
            <div v-else class="password-strength mt-2">
              <div class="strength-bar">
                <div 
                  class="strength-fill" 
                  :class="getPasswordStrengthClass()"
                  :style="{ width: getPasswordStrength() + '%' }"
                ></div>
              </div>
              <small class="strength-text">
                {{ getPasswordStrengthText() }}
              </small>
            </div>
          </div>

          <!-- Repetir Nova Senha -->
          <div class="form-group mb-4">
            <label class="form-label">
              <i class="fas fa-lock"></i>
              Confirmar Nova Senha <span class="text-danger">*</span>
            </label>
            <div class="password-input-container">
              <FieldInput
                :type="mostrarRepetirSenha ? 'text' : 'password'"
                required
                v-model="repetirSenhaNova"
                placeholder="Repita sua nova senha"
                @input="validarSenha"
                :class="{ 'is-invalid': erroRepeticao }"
                class="senha-input"
              />
              <button 
                type="button" 
                class="btn-toggle-password"
                @click="mostrarRepetirSenha = !mostrarRepetirSenha"
              >
                <i :class="mostrarRepetirSenha ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <small v-if="erroRepeticao" class="text-danger d-block mt-2">
              <i class="fas fa-exclamation-circle"></i>
              {{ erroRepeticao }}
            </small>
            <small v-else-if="repetirSenhaNova && !erroRepeticao" class="text-success d-block mt-2">
              <i class="fas fa-check-circle"></i>
              As senhas coincidem
            </small>
          </div>

          <!-- Botão de Envio -->
          <div class="text-center mt-4">
            <button 
              type="submit" 
              class="btn btn-primary btn-lg" 
              :disabled="!podeEnviar || salvando"
            >
              <i :class="salvando ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
              {{ salvando ? 'Salvando...' : 'Salvar Nova Senha' }}
            </button>
            <p class="text-muted mt-3 small">
              <i class="fas fa-info-circle"></i>
              Após alterar a senha, você continuará logado
            </p>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios"
import { mapGetters } from "vuex"
import FieldInput from "@/components/fields/FieldInput"

export default {
  name: 'AlterarSenha',
  components: {
    FieldInput,
  },

  data() {
    return {
      senhaNova: "",
      senhaAntiga: "",
      repetirSenhaNova: "",
      erroSenha: "",
      erroRepeticao: "",
      salvando: false,
      mostrarSenhaAntiga: false,
      mostrarSenhaNova: false,
      mostrarRepetirSenha: false,
    }
  },

  computed: {
    ...mapGetters({ profile: "profile/getProfile" }),

    podeEnviar() {
      return (
        this.senhaAntiga.length > 0 &&
        this.senhaNova.length >= 6 &&
        this.repetirSenhaNova === this.senhaNova &&
        !this.erroSenha &&
        !this.erroRepeticao
      )
    },
  },

  methods: {
    validarSenha() {
      // Valida se a senha atende aos requisitos mínimos
      if (this.senhaNova.length === 0) {
        this.erroSenha = ""
      } else if (this.senhaNova.length < 6) {
        this.erroSenha = "A senha deve ter pelo menos 6 caracteres."
      } else {
        this.erroSenha = ""
      }

      // Valida se a senha e a repetição são iguais
      if (this.repetirSenhaNova.length === 0) {
        this.erroRepeticao = ""
      } else if (this.repetirSenhaNova !== this.senhaNova) {
        this.erroRepeticao = "As senhas não coincidem."
      } else {
        this.erroRepeticao = ""
      }
    },

    getPasswordStrength() {
      const password = this.senhaNova
      if (password.length === 0) return 0
      
      let strength = 0
      
      // Comprimento
      if (password.length >= 6) strength += 25
      if (password.length >= 8) strength += 15
      if (password.length >= 10) strength += 10
      
      // Letras minúsculas
      if (/[a-z]/.test(password)) strength += 15
      
      // Letras maiúsculas
      if (/[A-Z]/.test(password)) strength += 15
      
      // Números
      if (/[0-9]/.test(password)) strength += 10
      
      // Caracteres especiais
      if (/[^a-zA-Z0-9]/.test(password)) strength += 10
      
      return Math.min(strength, 100)
    },

    getPasswordStrengthClass() {
      const strength = this.getPasswordStrength()
      if (strength < 40) return 'strength-weak'
      if (strength < 70) return 'strength-medium'
      return 'strength-strong'
    },

    getPasswordStrengthText() {
      const strength = this.getPasswordStrength()
      if (strength === 0) return ''
      if (strength < 40) return 'Senha fraca'
      if (strength < 70) return 'Senha média'
      return 'Senha forte'
    },

    async cadastrarSenha() {
      try {
        this.salvando = true
        this.$sweet.sweetWait("Alterando sua senha...")

        const params = {
          novaSenha: this.senhaNova,
          senhaAtual: this.senhaAntiga,
        }

        // console.log('Alterando senha...')

        const { status } = await axios.put(`/ps/candidato/trocar-senha`, params)

        if (status > 299) {
          throw new Error('Erro ao alterar senha')
        }

        // Limpar campos
        this.senhaNova = ""
        this.senhaAntiga = ""
        this.repetirSenhaNova = ""
        this.erroSenha = ""
        this.erroRepeticao = ""
        
        this.$sweet.sweetSuccess("Senha alterada com sucesso!")
      } catch (error) {
        console.error('Erro ao trocar senha:', error)
        
        let errorMessage = "Erro ao trocar a senha. Verifique se a senha atual está correta."
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        }
        
        this.$sweet.sweetError(errorMessage)
      } finally {
        this.salvando = false
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.area-senha
  background-color: #fff
  padding: 25px 15px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    font-weight: 600
    text-align: center
    color: #2c3e50
    margin-bottom: 25px
    
    i
      margin-right: 10px
      color: #007bff

  h6
    font-size: 16px
    font-weight: 600
    color: #2c3e50
    margin-bottom: 15px
    
    i
      margin-right: 8px
      color: #17a2b8

// Info box
.info-box
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)
  padding: 20px
  border-radius: 8px
  border-left: 4px solid #17a2b8
  
  ul
    margin: 0
    padding-left: 0
    list-style: none
    
    li
      padding: 8px 0
      color: #0c5460
      font-size: 14px
      line-height: 1.6
      
      i
        margin-right: 8px
        color: #17a2b8

// Form container
.senha-form-container
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  padding: 30px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.05)

// Form elements
.form-group
  margin-bottom: 20px

.form-label
  font-weight: 600
  color: #2c3e50
  margin-bottom: 8px
  display: block
  font-size: 14px
  
  i
    margin-right: 6px
    color: #007bff
  
  .text-danger
    margin-left: 2px

// Password input container
.password-input-container
  position: relative
  display: flex
  align-items: center
  
  .senha-input
    flex: 1
    padding-right: 45px !important

.btn-toggle-password
  position: absolute
  right: 10px
  background: none
  border: none
  color: #6c757d
  cursor: pointer
  padding: 8px
  border-radius: 4px
  transition: all 0.2s ease
  
  &:hover
    color: #007bff
    background: rgba(0, 123, 255, 0.1)
  
  i
    font-size: 16px

// Password strength
.password-strength
  .strength-bar
    height: 6px
    background: #e9ecef
    border-radius: 3px
    overflow: hidden
    margin-bottom: 5px
    
    .strength-fill
      height: 100%
      transition: all 0.3s ease
      border-radius: 3px
      
      &.strength-weak
        background: linear-gradient(90deg, #dc3545, #f86c6b)
        width: 33%
      
      &.strength-medium
        background: linear-gradient(90deg, #ffc107, #ffdb4d)
        width: 66%
      
      &.strength-strong
        background: linear-gradient(90deg, #28a745, #5eba7d)
        width: 100%
  
  .strength-text
    font-size: 12px
    color: #6c757d
    font-weight: 500

// Validation messages
.text-danger, .text-success
  font-size: 13px
  
  i
    margin-right: 4px

.is-invalid
  border-color: #dc3545 !important
  
  &:focus
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important

// Buttons
.btn
  border-radius: 6px
  padding: 10px 20px
  font-weight: 500
  transition: all 0.3s ease
  border: none
  
  &:hover
    transform: translateY(-2px)
    box-shadow: 0 4px 8px rgba(0,0,0,0.2)
  
  &:disabled
    opacity: 0.6
    cursor: not-allowed
    transform: none
  
  i
    margin-right: 8px
  
  &.btn-lg
    padding: 12px 24px
    font-size: 16px

// Responsividade
@media (max-width: 768px)
  .area-senha
    padding: 15px 10px
    
    h5
      font-size: 18px
  
  .senha-form-container
    padding: 20px

@media (max-width: 576px)
  .info-box
    padding: 15px
    
    ul li
      font-size: 13px
  
  .btn.btn-lg
    width: 100%
</style>
